import { Alert, Button, Card, Form, Input, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createAdminAnnouncement,
  deleteAdminAnnouncement,
  listAdminAnnouncements,
  updateAdminAnnouncement,
} from '../services/api';
import type { Announcement, AnnouncementCreate, AnnouncementSeverity, CurrentUser } from '../types/incident';

const { Paragraph, Text } = Typography;

type AdminAnnouncementsPageProps = {
  currentUser: CurrentUser | null;
};

type AnnouncementFormValues = {
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  published_at?: string;
  expires_at?: string;
};

const severityColors: Record<AnnouncementSeverity, string> = {
  info: 'blue',
  warning: 'gold',
  critical: 'red',
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const toDatetimeLocalValue = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toIsoOrNull = (value?: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const buildPayload = (values: AnnouncementFormValues): AnnouncementCreate => ({
  title: values.title.trim(),
  body: values.body.trim(),
  severity: values.severity,
  published_at: toIsoOrNull(values.published_at) || new Date().toISOString(),
  expires_at: toIsoOrNull(values.expires_at),
});

const AdminAnnouncementsPage = ({ currentUser }: AdminAnnouncementsPageProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AnnouncementFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const isSystemAdmin = useMemo(
    () => currentUser?.assignments.some((assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active) || false,
    [currentUser?.assignments]
  );

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      setAnnouncements(await listAdminAnnouncements());
    } catch {
      messageApi.error(t('adminAnnouncements.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadAnnouncements();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, loadAnnouncements]);

  const resetForm = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ severity: 'info' });
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingId(announcement.id);
    form.setFieldsValue({
      title: announcement.title,
      body: announcement.body,
      severity: announcement.severity,
      published_at: toDatetimeLocalValue(announcement.published_at),
      expires_at: toDatetimeLocalValue(announcement.expires_at),
    });
  };

  const handleSubmit = async (values: AnnouncementFormValues) => {
    setSaving(true);
    try {
      const payload = buildPayload(values);
      if (editingId) {
        const updated = await updateAdminAnnouncement(editingId, payload);
        setAnnouncements((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        messageApi.success(t('adminAnnouncements.messages.updated'));
      } else {
        const created = await createAdminAnnouncement(payload);
        setAnnouncements((current) => [created, ...current]);
        messageApi.success(t('adminAnnouncements.messages.created'));
      }
      resetForm();
      await loadAnnouncements();
    } catch {
      messageApi.error(t('adminAnnouncements.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (announcementId: string) => {
    try {
      await deleteAdminAnnouncement(announcementId);
      setAnnouncements((current) => current.filter((item) => item.id !== announcementId));
      messageApi.success(t('adminAnnouncements.messages.deleted'));
      if (editingId === announcementId) {
        resetForm();
      }
    } catch {
      messageApi.error(t('adminAnnouncements.messages.deleteFailed'));
    }
  };

  const columns: ColumnsType<Announcement> = [
    {
      title: t('adminAnnouncements.table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (_value, announcement) => (
        <Space direction="vertical" size={2}>
          <Text strong>{announcement.title}</Text>
          <Paragraph className="adminAnnouncementBodyPreview" type="secondary">
            {announcement.body}
          </Paragraph>
        </Space>
      ),
    },
    {
      title: t('adminAnnouncements.table.severity'),
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (severity: AnnouncementSeverity) => (
        <Tag color={severityColors[severity]}>{t(`announcements.severity.${severity}`)}</Tag>
      ),
    },
    {
      title: t('adminAnnouncements.table.publishedAt'),
      dataIndex: 'published_at',
      key: 'published_at',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('adminAnnouncements.table.expiresAt'),
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('adminAnnouncements.table.actions'),
      key: 'actions',
      width: 150,
      render: (_value, announcement) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(announcement)}>
            {t('adminAnnouncements.actions.edit')}
          </Button>
          <Popconfirm
            title={t('adminAnnouncements.actions.deleteConfirmTitle')}
            description={t('adminAnnouncements.actions.deleteConfirmDescription')}
            okText={t('common.yes')}
            cancelText={t('common.cancel')}
            onConfirm={() => handleDelete(announcement.id)}
          >
            <Button danger size="small">
              {t('adminAnnouncements.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isSystemAdmin) {
    return (
      <div className="page">
        {contextHolder}
        <Alert type="warning" showIcon message={t('adminAnnouncements.forbidden')} />
      </div>
    );
  }

  return (
    <div className="page adminAnnouncementsPage">
      {contextHolder}
      <div className="pageHeader">
        <p>{t('adminAnnouncements.description')}</p>
      </div>

      <Card
        className="adminAnnouncementFormCard"
        title={editingId ? t('adminAnnouncements.form.editTitle') : t('adminAnnouncements.form.createTitle')}
      >
        <Form form={form} layout="vertical" initialValues={{ severity: 'info' }} onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label={t('adminAnnouncements.form.title')}
            rules={[{ required: true, message: t('validation.required', { label: t('adminAnnouncements.form.title') }) }]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="body"
            label={t('adminAnnouncements.form.body')}
            rules={[{ required: true, message: t('validation.required', { label: t('adminAnnouncements.form.body') }) }]}
          >
            <Input.TextArea rows={5} maxLength={10000} showCount />
          </Form.Item>
          <Space className="adminAnnouncementFieldRow" size={16} wrap>
            <Form.Item name="severity" label={t('adminAnnouncements.form.severity')}>
              <Select
                className="adminAnnouncementSeveritySelect"
                options={[
                  { value: 'info', label: t('announcements.severity.info') },
                  { value: 'warning', label: t('announcements.severity.warning') },
                  { value: 'critical', label: t('announcements.severity.critical') },
                ]}
              />
            </Form.Item>
            <Form.Item name="published_at" label={t('adminAnnouncements.form.publishedAt')}>
              <Input className="adminAnnouncementDateInput" type="datetime-local" />
            </Form.Item>
            <Form.Item name="expires_at" label={t('adminAnnouncements.form.expiresAt')}>
              <Input className="adminAnnouncementDateInput" type="datetime-local" />
            </Form.Item>
          </Space>
          <Space className="adminAnnouncementActionRow">
            <Button type="primary" htmlType="submit" loading={saving}>
              {editingId ? t('adminAnnouncements.actions.update') : t('adminAnnouncements.actions.create')}
            </Button>
            {editingId && <Button onClick={resetForm}>{t('common.cancel')}</Button>}
          </Space>
        </Form>
      </Card>

      <Card className="adminAnnouncementsTableCard" title={t('adminAnnouncements.listTitle')}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={announcements}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
};

export default AdminAnnouncementsPage;
